import { Injectable, inject } from '@angular/core';
import { COMPONENTS, EXCLUDED_CONTROL_PROPS, EXT_KEYS, EXT_PREFIX } from '../constants/fhir.constants';
import {
  ControlMetadataInfo,
  FHIR_CONVERTER_CONFIG,
  IControlCard,
  IFhirExtensionBuilder,
} from '../interfaces/fhir-converter.interfaces';
import { FHIRExtension } from '../models/fhir.model';
import { sanitizeChoiceValueInConfig } from '../utilities/choice-option-matcher';

/**
 * FHIR Extension Builder Service
 *
 * Responsible for creating FHIR R4 compliant extensions. Centralizes all extension building logic for consistency.
 *
 * Uses short URN prefixes and abbreviated keys for size optimization:
 *
 * - Base: urn:ccs:ext
 * - UI Config: urn:ccs:ext:ui
 * - Nested keys: c (component), p (package), i (icon), cat (category)
 */
@Injectable({
  providedIn: 'root',
})
export class FhirExtensionBuilder implements IFhirExtensionBuilder {
  /** Injected config (optional) */
  protected readonly injectedConfig = inject(FHIR_CONVERTER_CONFIG, { optional: true });

  /** Resolved extension prefix - protected for subclass access */
  protected readonly extPrefix: string;

  /** Resolved ui-config extension URL */
  protected readonly uiConfigExt: string;

  constructor() {
    const config = this.injectedConfig;
    this.extPrefix = config?.prefix || EXT_PREFIX;
    this.uiConfigExt = `${this.extPrefix}:${config?.uiConfig || 'ui'}`;
  }

  /**
   * Build section-level extensions for designer questionnaires
   *
   * @param icon - Optional icon identifier
   * @param assessmentStrategy - Optional assessment strategy name
   * @param assessmentPosition - Optional assessment position ("inside" or "outside")
   * @param sectionId - Optional section ID to persist through FHIR roundtrip
   * @returns Array of FHIR extensions
   */
  buildSectionExtensions(
    icon?: string,
    assessmentStrategy?: string | null,
    assessmentPosition?: string | null,
    sectionId?: string,
    required?: boolean,
    questionTitle?: string,
    isVisible?: boolean,
    pdfExport?: boolean,
    aiExtraction?: boolean,
    amend?: boolean,
    tag?: string[]
  ): FHIRExtension[] {
    const uiConfig: Record<string, unknown> = {};

    // Always add section component
    uiConfig[EXT_KEYS.COMPONENT] = COMPONENTS.SECTION;

    // Always add icon (even if empty string)
    uiConfig[EXT_KEYS.ICON] = icon || '';

    // Always add assessment strategy (even if null)
    uiConfig['assessmentStrategy'] = assessmentStrategy || null;

    // Add assessment position only if explicitly set
    uiConfig['assessmentPosition'] = assessmentPosition || null;

    // Persist section ID for FHIR roundtrip (stable URL routing)
    if (sectionId) {
      uiConfig['sectionId'] = sectionId;
    }

    if (required !== undefined) {
      uiConfig['required'] = required;
    }

    if (questionTitle !== undefined) {
      uiConfig['questionTitle'] = questionTitle;
    }

    if (isVisible !== undefined) {
      uiConfig['isVisible'] = isVisible;
    }

    if (pdfExport !== undefined) {
      uiConfig['pdfExport'] = pdfExport;
    }

    if (aiExtraction !== undefined) {
      uiConfig['aiExtraction'] = aiExtraction;
    }

    if (amend !== undefined) {
      uiConfig['amend'] = amend;
    }

    if (tag && Array.isArray(tag) && tag.length > 0) {
      uiConfig['tag'] = tag;
    }

    // uiConfig['showStatus'] = true;

    return [
      {
        url: this.uiConfigExt,
        valueString: JSON.stringify(uiConfig),
      },
    ];
  }

  /**
   * Build control-level ui-config extension as a single JSON string Optimized for readability and minimal JSON
   * footprint
   *
   * @param control - The source control
   * @param metadata - Control metadata from registry
   * @returns Array containing single ui-config extension with JSON valueString
   */
  buildControlExtensions(control: IControlCard, metadata: ControlMetadataInfo | null): FHIRExtension[] {
    const uiConfig: Record<string, unknown> = {};

    // Add component name from registry
    if (metadata?.componentType) {
      uiConfig[EXT_KEYS.COMPONENT] = metadata.componentType;
    }

    // Add package name from registry
    if (metadata?.package) {
      uiConfig[EXT_KEYS.PACKAGE] = metadata.package;
    }

    // Add category from registry
    if (metadata?.category) {
      uiConfig[EXT_KEYS.CATEGORY] = metadata.category;
    }

    // Add control properties (exclude standard ones)
    this.addControlPropertiesToConfig(control, uiConfig);

    // Add icon for all controls (componentProps is source-of-truth; fall back to root icon)
    if (uiConfig[EXT_KEYS.ICON] === undefined) {
      const icon =
        (control.componentProps as Record<string, unknown> | undefined)?.['icon'] ??
        (control as Record<string, unknown>)['icon'];
      if (typeof icon === 'string' && icon.length > 0) {
        uiConfig[EXT_KEYS.ICON] = icon;
      }
    }

    // Sanitize value for radio and choice controls:
    // If uiConfig has a 'value' property but it does not match any valid option or special option,
    // remove it so that invalid or legacy values (e.g. 'option_1') are not saved.
    sanitizeChoiceValueInConfig(control, metadata, uiConfig);

    // Only return if there are properties
    if (Object.keys(uiConfig).length === 0) {
      return [];
    }

    return [
      {
        url: this.uiConfigExt,
        valueString: JSON.stringify(uiConfig),
      },
    ];
  }

  /**
   * Extract extension value by suffix
   *
   * @param extensions - Array of extensions to search
   * @param suffix - URL suffix to match
   * @returns Extension value or undefined
   */
  extractExtensionValue(extensions: FHIRExtension[] | undefined, suffix: string): string | undefined {
    if (!extensions) return undefined;

    const ext = extensions.find((e) => e.url.endsWith(`/${suffix}`) || e.url.endsWith(`:${suffix}`));
    return ext?.valueString;
  }

  /**
   * Extract Harmony component properties from extensions
   *
   * @param extensions - Array of extensions to search
   * @returns Harmony props object or undefined
   */
  extractHarmonyProps(extensions: FHIRExtension[] | undefined): Record<string, unknown> | undefined {
    if (!extensions) return undefined;

    const harmonyExtension = extensions.find(
      (ext) => ext.url === `${this.extPrefix}:${EXT_KEYS.HARMONY}` || ext.url.endsWith(':harmonyProperties')
    );

    if (!harmonyExtension?.extension) return undefined;

    const harmonyProps: Record<string, unknown> = {};

    for (const childExt of harmonyExtension.extension) {
      const propName = childExt.url;

      if (childExt.valueBoolean !== undefined) {
        harmonyProps[propName] = childExt.valueBoolean;
      } else if (childExt.valueString !== undefined) {
        try {
          harmonyProps[propName] = JSON.parse(childExt.valueString);
        } catch {
          harmonyProps[propName] = childExt.valueString;
        }
      } else if (childExt.valueInteger !== undefined) {
        harmonyProps[propName] = childExt.valueInteger;
      }
    }

    return Object.keys(harmonyProps).length > 0 ? harmonyProps : undefined;
  }

  /** Add control properties to config object - optimized for performance */
  protected addControlPropertiesToConfig(control: IControlCard, config: Record<string, unknown>): void {
    const excludeProps = new Set(['isRequired', 'fhirPath']);
    const dataMode = (control.componentProps as any)?.dataMode || (control as any).dataMode;
    const skipStaticData = dataMode === 'api';

    const componentProps =
      control.componentProps && typeof control.componentProps === 'object'
        ? (control.componentProps as Record<string, unknown>)
        : undefined;

    // Add properties from componentProps (source-of-truth)
    if (componentProps) {
      for (const [key, value] of Object.entries(componentProps)) {
        // Skip options and staticData when dataMode is 'api' - they are only for static/preview mode
        if ((key === 'options' || key === 'staticData') && skipStaticData) continue;
        if (!excludeProps.has(key) && value !== undefined && value !== null) {
          if (key === 'tag') {
            if (Array.isArray(value) && value.length > 0) {
              config[key] = value;
            }
          } else {
            config[key] = value;
          }
        }
      }
    }

    if (config.required === undefined && (control as any).isRequired !== undefined) {
      config.required = (control as any).isRequired;
    }

    // Add direct control properties (legacy/back-compat).
    // IMPORTANT: Never override a value already provided by componentProps.
    // If componentProps owns the key, it is always the source-of-truth — even when null.
    // A null value in componentProps means "explicitly cleared" (e.g. Default Selection = None)
    // and must not be shadowed by a stale value on the control root.
    for (const [key, value] of Object.entries(control)) {
      if (componentProps && Object.prototype.hasOwnProperty.call(componentProps, key)) {
        continue;
      }

      if (
        !EXCLUDED_CONTROL_PROPS.has(key) &&
        !(key === 'staticData' && skipStaticData) &&
        value !== undefined &&
        value !== null
      ) {
        if (key === 'tag') {
          if (Array.isArray(value) && value.length > 0) {
            config[key] = value;
          }
        } else {
          config[key] = value;
        }
      }
    }
  }

  /** Add control properties as nested extensions Uses for-loop for performance in hot paths */
  protected addControlProperties(control: IControlCard, nestedExts: FHIRExtension[]): void {
    const keys = Object.keys(control);

    // Check if dataMode is 'api' - if so, skip staticData in FHIR export
    const dataMode = (control.componentProps as any)?.dataMode || (control as any).dataMode;
    const skipStaticData = dataMode === 'api';

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];

      if (EXCLUDED_CONTROL_PROPS.has(key)) continue;
      // Skip staticData when dataMode is 'api' - static data is only for static/preview mode
      if (key === 'staticData' && skipStaticData) continue;

      const value = control[key];
      if (value === undefined || value === null) continue;

      const ext = this.createValueExtension(key, value);
      if (ext) {
        nestedExts.push(ext);
      }
    }
  }

  /** Add Harmony properties as complex extension */
  protected addHarmonyExtension(harmonyProps: Record<string, unknown> | undefined, extensions: FHIRExtension[]): void {
    if (!harmonyProps || Object.keys(harmonyProps).length === 0) {
      return;
    }

    const nestedExtensions: FHIRExtension[] = [];

    for (const [key, value] of Object.entries(harmonyProps)) {
      if (value === undefined || value === null) continue;

      const ext = this.createValueExtension(key, value);
      if (ext) {
        nestedExtensions.push(ext);
      }
    }

    if (nestedExtensions.length > 0) {
      extensions.push({
        url: `${this.extPrefix}:${EXT_KEYS.HARMONY}`,
        extension: nestedExtensions,
      });
    }
  }

  /** Create extension with appropriate value type */
  protected createValueExtension(key: string, value: unknown): FHIRExtension | null {
    const valueType = typeof value;

    switch (valueType) {
      case 'string':
        return { url: key, valueString: value as string };
      case 'number':
        return { url: key, valueInteger: value as number };
      case 'boolean':
        return { url: key, valueBoolean: value as boolean };
      case 'object':
        if (!Array.isArray(value)) {
          return { url: key, valueString: JSON.stringify(value) };
        }
        return null;
      default:
        return null;
    }
  }

  /** Get the extension prefix */
  getExtPrefix(): string {
    return this.extPrefix;
  }

  /** Get the UI config extension URL */
  getUiConfigExt(): string {
    return this.uiConfigExt;
  }
}
